import styled from 'foundations-theming/utils';
import { useIsIdle } from '../utils/useIsIdle';
import TruncateDiv from './TruncateDiv';
import { useMemo } from 'react';
import { memo } from 'react';
import { DefaultLinkComponent } from './labels/LabelCell';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { Avatar } from 'avatar-components/Avatar';
import { CONTACT_TYPE_ID, COMPANY_TYPE_ID, LEAD_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { jsx as _jsx } from "react/jsx-runtime";
const AvatarContainer = styled.div.withConfig({
  displayName: "AvatarCell__AvatarContainer"
})(["height:24px;width:24px;"]);
const AvatarPlaceholder = styled.div.withConfig({
  displayName: "AvatarCell__AvatarPlaceholder"
})(["background-color:", ";border-color:transparent;border-radius:50%;width:100%;height:100%;"], ({
  theme
}) => theme.color['fill-surface-recessed']);
/**
 * This component is meant to render an avatar around "children" you pass in.
 * It doesnt render a link, you have to do that on your own.
 *
 * ONLY CONTACT_TYPE_ID and COMPANY_TYPE_ID have avatars. Please only use this when the objectTypeId is CONTACT_TYPE_ID or COMPANY_TYPE_ID
 *
 * You should use this if you want the little avatar circle, but you want to manage most everything else
 * If you want something that gets you the "suggested" experience and you dont need any customization, use "AvatarCell" instead
 */
export const AvatarCellWrapper = ({
  displayName,
  id,
  objectTypeId,
  object,
  isHubSpotUserEmail = false,
  children
}) => {
  const lookupProps = useMemo(() => {
    if (isHubSpotUserEmail) {
      return {
        type: 'hubSpotUserEmail',
        hubSpotUserEmail: id
      };
    }
    if (objectTypeId === CONTACT_TYPE_ID) {
      return {
        type: 'vid',
        vid: Number(id)
      };
    }
    if (objectTypeId === LEAD_TYPE_ID) {
      var _properties$hs_primar, _properties$hs_primar2;
      if (!object) {
        return {
          type: 'companyId',
          companyId: Number(id)
        };
      }
      const {
        properties
      } = object;
      const primaryContactId = (_properties$hs_primar = properties['hs_primary_contact_id']) === null || _properties$hs_primar === void 0 ? void 0 : _properties$hs_primar.value;
      if (primaryContactId) {
        return {
          type: 'vid',
          vid: Number(primaryContactId)
        };
      }
      const primaryCompanyId = (_properties$hs_primar2 = properties['hs_primary_company_id']) === null || _properties$hs_primar2 === void 0 ? void 0 : _properties$hs_primar2.value;
      if (primaryCompanyId) {
        return {
          type: 'companyId',
          companyId: Number(primaryCompanyId)
        };
      }
    }
    return {
      type: 'companyId',
      companyId: Number(id)
    };
  }, [objectTypeId, id, isHubSpotUserEmail, object]);
  const isIdle = useIsIdle();
  const avatar = isIdle && id ? /*#__PURE__*/_jsx(Avatar, Object.assign({}, lookupProps, {
    displayName: displayName,
    size: "xs"
  })) : /*#__PURE__*/_jsx(AvatarPlaceholder, {
    "data-test-id": "avatar-placeholder"
  });
  return /*#__PURE__*/_jsx(MediaObject, {
    align: "center",
    itemLeft: id && avatar,
    MediaLeft: AvatarContainer,
    children: /*#__PURE__*/_jsx(TruncateDiv, {
      children: children
    })
  });
};

/**
 * This component will take in props and then render an avatar and link
 *
 * Its an opinionated easy to use component - if you find it doesnt work for you, consider using AvatarCellWrapper which gives you more control
 */
export const AvatarCell = ({
  // default to link component here as well in case this component is called externally
  // so that it matches the expected behavior
  LinkComponent = DefaultLinkComponent,
  type,
  id,
  href,
  displayName,
  onClick,
  objectTypeId,
  children
}) => {
  // Determine which objectTypeId to use based on which prop is provided
  const {
    resolvedObjectTypeId,
    isHubSpotUserEmail
  } = useMemo(() => {
    if (objectTypeId) {
      return {
        resolvedObjectTypeId: objectTypeId,
        isHubSpotUserEmail: false
      };
    }
    if (type === 'vid') {
      return {
        resolvedObjectTypeId: CONTACT_TYPE_ID,
        isHubSpotUserEmail: false
      };
    }
    if (type === 'companyId') {
      return {
        resolvedObjectTypeId: COMPANY_TYPE_ID,
        isHubSpotUserEmail: false
      };
    }
    if (type === 'hubSpotUserEmail') {
      return {
        resolvedObjectTypeId: CONTACT_TYPE_ID,
        isHubSpotUserEmail: true
      };
    }
    // Default fallback
    return {
      resolvedObjectTypeId: CONTACT_TYPE_ID,
      isHubSpotUserEmail: false
    };
  }, [type, objectTypeId]);
  const isClickable = href || onClick;
  return /*#__PURE__*/_jsx(AvatarCellWrapper, {
    displayName: displayName,
    id: id,
    objectTypeId: resolvedObjectTypeId,
    isHubSpotUserEmail: isHubSpotUserEmail,
    children: isClickable ? /*#__PURE__*/_jsx(LinkComponent, {
      role: "link",
      href: href,
      onClick: onClick,
      children: children
    }) : children
  });
};
export default /*#__PURE__*/memo(AvatarCell);